/**
 * NetBoxRoleMappingEditor — Редактор маппингов ролей NetBox → equipment_type
 * Sprint 3
 */
import { useEffect, useState, useCallback } from 'react'
import { Plus, Trash2, Save, RefreshCw, Tag, ChevronDown, ChevronUp, Info } from 'lucide-react'
import { api } from '../../utils/api'
import { toast } from 'react-hot-toast'

interface RoleMapping {
  id?: number
  nb_role_slug: string
  nb_device_type_slug: string
  equipment_type: string
  note: string
}

interface Props {
  dcCode: string
}

const EQUIPMENT_TYPES = [
  'server', 'switch', 'router', 'pdu', 'patch_panel',
  'ups', 'kvm', 'firewall', 'storage', 'other'
]

const DEFAULT_MAPPINGS: RoleMapping[] = [
  { nb_role_slug: 'server', nb_device_type_slug: '', equipment_type: 'server', note: '' },
  { nb_role_slug: 'leaf', nb_device_type_slug: '', equipment_type: 'switch', note: '' },
  { nb_role_slug: 'spine', nb_device_type_slug: '', equipment_type: 'switch', note: '' },
]

export function NetBoxRoleMappingEditor({ dcCode }: Props) {
  const [mappings, setMappings] = useState<RoleMapping[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const [dirty, setDirty] = useState(false)

  const load = useCallback(async () => {
    if (!dcCode) return
    setLoading(true)
    try {
      const res = await api.get(`/api/integrations/netbox/role-mapping?dc_code=${dcCode}`)
      const data = res.data.mappings || []
      setMappings(data.length > 0 ? data : DEFAULT_MAPPINGS)
      setDirty(false)
    } catch {
      setMappings(DEFAULT_MAPPINGS)
    } finally {
      setLoading(false)
    }
  }, [dcCode])

  useEffect(() => {
    if (expanded) load()
  }, [expanded, load])

  const update = (idx: number, field: keyof RoleMapping, value: string) => {
    setMappings(prev => prev.map((m, i) => i === idx ? { ...m, [field]: value } : m))
    setDirty(true)
  }

  const addRow = () => {
    setMappings(prev => [...prev, { nb_role_slug: '', nb_device_type_slug: '', equipment_type: 'server', note: '' }])
    setDirty(true)
  }

  const removeRow = (idx: number) => {
    setMappings(prev => prev.filter((_, i) => i !== idx))
    setDirty(true)
  }

  const save = async () => {
    const valid = mappings.filter(m => m.nb_role_slug.trim())
    setSaving(true)
    try {
      await api.put('/api/integrations/netbox/role-mapping', {
        dc_code: dcCode,
        mappings: valid.map(m => ({
          nb_role_slug: m.nb_role_slug.trim(),
          nb_device_type_slug: m.nb_device_type_slug.trim() || null,
          equipment_type: m.equipment_type,
          note: m.note.trim() || null,
        }))
      })
      toast.success(`Сохранено ${valid.length} маппингов`)
      setDirty(false)
      load()
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Ошибка сохранения')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      {/* Header / Toggle */}
      <button
        onClick={() => setExpanded(e => !e)}
        className="w-full flex items-center gap-2 px-3 py-2.5 bg-bg-panel hover:bg-white/5 transition-all text-left"
      >
        <Tag size={14} className="text-accent-primary flex-shrink-0" />
        <span className="text-sm font-medium text-text-primary flex-1">Маппинг ролей NetBox</span>
        {dirty && <span className="w-2 h-2 rounded-full bg-yellow-400" title="Есть несохранённые изменения" />}
        {expanded ? <ChevronUp size={14} className="text-text-secondary" /> : <ChevronDown size={14} className="text-text-secondary" />}
      </button>

      {expanded && (
        <div className="border-t border-border bg-bg-base p-3 space-y-3">
          {/* Info */}
          <div className="flex items-start gap-2 text-xs text-text-secondary bg-blue-500/5 border border-blue-500/15 rounded p-2">
            <Info size={12} className="flex-shrink-0 mt-0.5 text-blue-400" />
            <span>NetBox Device Role slug → equipment_type в приложении. При импорте используется для определения типа оборудования. Если slug не совпадает — используется тип «server» по умолчанию.</span>
          </div>

          {/* Table */}
          {loading ? (
            <div className="text-center py-4 text-text-secondary text-sm">Загрузка...</div>
          ) : (
            <>
              <div className="space-y-1.5">
                {/* Column headers */}
                <div className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 text-xs text-text-secondary px-1">
                  <span>NetBox Role Slug</span>
                  <span>Device Type (опц.)</span>
                  <span>Equipment Type</span>
                  <span></span>
                </div>
                {/* Rows */}
                {mappings.map((m, idx) => (
                  <div key={idx} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 items-center">
                    <input
                      value={m.nb_role_slug}
                      onChange={e => update(idx, 'nb_role_slug', e.target.value)}
                      placeholder="server"
                      className="px-2 py-1.5 rounded bg-bg-panel border border-border text-text-primary text-xs font-mono focus:outline-none focus:border-accent-primary/50 w-full"
                    />
                    <input
                      value={m.nb_device_type_slug}
                      onChange={e => update(idx, 'nb_device_type_slug', e.target.value)}
                      placeholder="любой"
                      className="px-2 py-1.5 rounded bg-bg-panel border border-border text-text-secondary text-xs font-mono focus:outline-none focus:border-accent-primary/50 w-full"
                    />
                    <select
                      value={m.equipment_type}
                      onChange={e => update(idx, 'equipment_type', e.target.value)}
                      className="px-2 py-1.5 rounded bg-bg-panel border border-border text-text-primary text-xs focus:outline-none focus:border-accent-primary/50 w-full"
                    >
                      {EQUIPMENT_TYPES.map(t => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                    <button
                      onClick={() => removeRow(idx)}
                      className="p-1.5 text-text-secondary hover:text-red-400 transition-colors"
                    ><Trash2 size={13}/></button>
                  </div>
                ))}
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 pt-1">
                <button
                  onClick={addRow}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded border border-dashed border-border text-text-secondary hover:text-text-primary hover:border-accent-primary/50 text-xs transition-all"
                >
                  <Plus size={12}/> Добавить строку
                </button>
                <button
                  onClick={save}
                  disabled={saving || !dirty}
                  className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded bg-accent-primary hover:bg-accent-primary/90 text-white text-xs font-medium disabled:opacity-50 transition-all"
                >
                  {saving ? <RefreshCw size={12} className="animate-spin"/> : <Save size={12}/>}
                  Сохранить
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}
